import { useState, useEffect } from 'react';
import { bool, func, string } from 'prop-types';
import classNames from 'classnames';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import { Modal, NamedLink } from '..';
import empty from './empty.svg';
import full from './full.svg';
import TermsOfService from '../TermsOfService/TermsOfService';
import { PrimaryButton } from '../index';
import css from './BarMissingInformation.module.css';
const EMAIL_VERIFICATION = 'EMAIL_VERIFICATION';
const SHIPPING_DETAILS = 'SHIPPING_DETAILS';
const SHOP_ADDRESS = 'SHOP_ADDRESS';
const ACCEPTED_TERMS = 'ACCEPTED_TERMS';
import {ContentOnly} from '../../containers/SellerTermsConditionsPage/SellerTermsConditionsPage';
const BarMissingInformation = ({
  rootClassName,
  className,
  currentUser,
  sendVerificationEmailInProgress,
  sendVerificationEmailError,
  onResendVerificationEmail,
  onManageDisableScrolling,
  updateTermsAccepted
}) => {
  const [missingInformation, setMissingInformation] = useState([]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [acceptButtonEnabled, setAcceptButtonEnabled] = useState(false);

  useEffect(() => {
    const user = ensureCurrentUser(currentUser);
    handleMissingInformationChecklist(user);
  }, [currentUser]);

  const handleMissingInformationChecklist = currentUser => {
    const missingItems = [];

    const emailUnverified = 
    (!!currentUser.id && !currentUser.attributes.emailVerified) ||
    currentUser?.attributes.pendingEmail;
    if (emailUnverified) {
      missingItems.push('Email not verified');
    }

    const shippingNotSet = 
     !!currentUser.id &&
      !currentUser.attributes.profile.publicData.shippingCountries?.length > 0 &&
      !currentUser.attributes.profile.publicData.shippingRanges?.length > 0;

    if (shippingNotSet) {
      missingItems.push('Shipping not set');
    }

    const shopAddressNotSet = 
    !!currentUser.id &&
    !currentUser.attributes.profile.publicData.country?.length > 0;

    if (shopAddressNotSet) {
      missingItems.push('Shop address not set');
    }

    const rulesNotAccepted = !currentUser?.attributes.profile?.publicData
      ?.termsAccepted;
    if (rulesNotAccepted) {
      missingItems.push('Rules not accepted');
    }

    setMissingInformation(missingItems);
  };

  const onAcceptTerms = termsAccepted => {
    updateTermsAccepted(termsAccepted)
      .then(() => {
        console.log('Terms accepted successfully');
        setShowTermsModal(false);
        window.location.reload();
      })
      .catch(error => {
        console.error('Error updating termsAccepted:', error);
        // Display error handling logic here
      });
  };

  const user = ensureCurrentUser(currentUser);
  const classes = classNames(
    rootClassName || css.root,
    className,
    css.emailMissingInformationContainer
  );

  let content = null;

  const currentUserLoaded = user && user.id;

  const templates = {
    'Email not verified': (
      <div>
        <p>
          Verify Email.{' '}
          <a
            onClick={onResendVerificationEmail}
            disabled={sendVerificationEmailInProgress}
          >
            Resend Verification Email
          </a>{' '}
        </p>

        {sendVerificationEmailError && (
          <p className="error">Error sending email. Please try again.</p>
        )}
      </div>
    ),
    'Shipping not set': (
      <div>
        <p>
          Set up your shipping information.{' '}
          <NamedLink name="ManageShippingPage">Set up Shipping</NamedLink>
        </p>
      </div>
    ),
    'Shop address not set': (
      <div>
        <p>
          Set your shop address.{' '}
          <NamedLink name="ShopDetailsPage">Set Shop Address</NamedLink>
        </p>
      </div>
    ),
    'Rules not accepted': (
      <div>
        <p>
          Review and accept Terms & Conditions.{' '}
          <a
            onClick={() => setShowTermsModal(true)}
            className={css.termsLink}
          >
            Accept Terms & Conditions
          </a>
        </p>
        {showTermsModal && (
          <Modal
            isOpen={showTermsModal}
            onClose={() => setShowTermsModal(false)}
            className={css.termsModal}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div
              className={css.termsContent}
              onScroll={e => {
                const { scrollTop, scrollHeight, clientHeight } = e.target;
                if (scrollTop + clientHeight >= scrollHeight) {
                  setAcceptButtonEnabled(true);
                }
              }}
            >
              <ContentOnly />
            </div>
            <PrimaryButton
              onClick={onAcceptTerms}
              className={css.acceptButton}
              disabled={!acceptButtonEnabled}
            >
              Accept Terms
            </PrimaryButton>
          </Modal>
        )}
      </div>
    ),
  };

  const allItems = [
    'Email not verified',
    'Shipping not set',
    'Shop address not set',
    'Rules not accepted',
  ];

  const completedTemplates = {
    'Email not verified': (
      <div className={css.done}>
        <p>Verify Email</p>
      </div>
    ),
    'Shipping not set': (
      <div className={css.done}>
        <p>Set up your shipping information.</p>
      </div>
    ),
    'Shop address not set': (
      <div className={css.done}>
        <p>Set up your shop address.</p>
      </div>
    ),
    'Rules not accepted': (
      <div className={css.done}>
        <p>Review and accept Terms & Conditions.</p>
      </div>
    ),
  };

  return (
    <div>
      {content}
      {missingInformation.length > 0 && (
        <div>
          <div className={classes}>
                      <h4 className={css.title}>Finish setting up your shop!</h4>

            <ul>
              {allItems.map((item, index) => (
                <li
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}
                >
                  <img
                  src={
                    !missingInformation.includes(item)
                    ? full
                    : empty
                  }
                  alt={item}
                  style={{
                    marginRight: '10px',
                    width: '20px',
                    height: '20px',
                  }}
                  />
                  
                  {missingInformation.includes(item)
                    ? templates[item] || <p>{item}</p>
                    : completedTemplates[item] || <p>{item} - Completed</p>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

BarMissingInformation.propTypes = {
  id: string.isRequired,
  className: string,
  rootClassName: string,
  containerClassName: string,

  currentUser: propTypes.currentUser,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailError: propTypes.error,
  sendVerificationEmailInProgress: bool.isRequired,
  onAcceptTerms: func.isRequired,
};

BarMissingInformation.displayName = 'BarMissingInformation';

export default BarMissingInformation;
